import { makeAutoObservable, runInAction } from 'mobx';

import { cmApi } from '@src/api';
import { preloaderStore } from '@stores/preloaderStore';
import type { CmApi, ReportGroupsDto } from '@src/api';

import { reportGroupsStore } from '../reportGroupsStore';

class ReportGroupEditStore {
  reportGroup: ReportGroupsDto | null;

  isNew = false;

  editMode = false;

  api: CmApi;

  hiddenEdit = false;

  hiddenDelete = false;

  hiddenClean = false;

  hiddenSave = false;

  constructor({ api }: { api: CmApi }) {
    this.api = api;
    this.reportGroup = null;

    makeAutoObservable(this);
  }

  get isEditMode() {
    return this.editMode || this.isNew;
  }

  setNew = (isNew = true) => {
    this.isNew = isNew;
    this.reportGroup = null;
  };

  setEditMode = (editMode = true) => {
    this.editMode = editMode;
  };

  save = async (reportGroup: ReportGroupsDto) => {
    preloaderStore.preloader(true);

    try {
      const { data } = await (this.isNew
        ? this.api.reportGroups.addReportGroup(reportGroup)
        : this.api.reportGroups.updateReportGroup(reportGroup));

      if (data) {
        reportGroupsStore.setIsChanged(true);

        if (this.isNew) {
          return { code: data.code };
        }
        runInAction(() => {
          this.reportGroup = data;
          this.setEditMode(false);
        });
      }
      return undefined;
    } finally {
      preloaderStore.preloader(false);
    }
  };

  delete = async () => {
    preloaderStore.preloader(true);
    if (this.reportGroup) {
      await this.api.reportGroups.deleteReportGroup([this.reportGroup.code ?? '']);
      reportGroupsStore.setIsChanged(true);
    }
    preloaderStore.preloader(false);
  };

  fetch = async (code?: string): Promise<void> => {
    if (!code) {
      return;
    }

    runInAction(() => {
      this.setEditMode(false);
      this.setNew(false);
    });

    preloaderStore.preloader(true);

    try {
      const { data } = await this.api.reportGroups.getReportGroup(code, { regulated: false });
      runInAction(() => {
        this.reportGroup = data;
      });
    } finally {
      preloaderStore.preloader(false);
    }
  };

  setHiddenEdit = (hiddenEdit = true): void => {
    this.hiddenEdit = hiddenEdit;
  };

  setHiddenDelete = (hiddenDelete = true): void => {
    this.hiddenDelete = hiddenDelete;
  };

  setHiddenClean = (hiddenClean = true): void => {
    this.hiddenClean = hiddenClean;
  };

  setHiddenSave = (hiddenSave = true): void => {
    this.hiddenSave = hiddenSave;
  };
}

export const reportGroupEditStore = new ReportGroupEditStore({ api: cmApi });
